(() => {
  const animateClass = 'animate-on-scroll';
  const defaultAnimation = 'fade-in-up';
  const elementsSeen = new WeakSet();
  const supportsObserver = 'IntersectionObserver' in window;
  const supportsMutationObserver = 'MutationObserver' in window;
  const prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const toNumber = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const reveal = (element) => {
    if (!element) {
      return;
    }

    const finalize = () => {
      element.classList.remove(animateClass);
      element.style.opacity = '';
    };

    if (prefersReducedMotion) {
      finalize();
      return;
    }

    const animationClass = element.dataset.animation || defaultAnimation;

    const applyAnimation = () => {
      element.classList.add(animationClass);

      const handleAnimationEnd = () => {
        finalize();
      };

      element.addEventListener('animationend', handleAnimationEnd, {
        once: true,
      });
      element.addEventListener('animationcancel', handleAnimationEnd, {
        once: true,
      });

      requestAnimationFrame(() => {
        const styles = window.getComputedStyle(element);
        if (
          styles.animationName === 'none' ||
          styles.animationDuration === '0s'
        ) {
          handleAnimationEnd();
        }
      });
    };

    const delay = toNumber(element.dataset.delay);
    if (delay > 0) {
      window.setTimeout(applyAnimation, delay);
    } else {
      applyAnimation();
    }
  };

  let observer;
  let mutationObserver;

  if (supportsObserver && !prefersReducedMotion) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        observer.unobserve(entry.target);
        reveal(entry.target);
      });
    });
  }

  const observeElements = (root = document) => {
    const isFragmentRoot =
      typeof DocumentFragment !== 'undefined' &&
      root instanceof DocumentFragment;
    const isElementRoot = root instanceof Element;
    const candidates = isElementRoot
      ? [root, ...root.querySelectorAll(`.${animateClass}`)]
      : isFragmentRoot
      ? [...root.querySelectorAll(`.${animateClass}`)]
      : [...document.querySelectorAll(`.${animateClass}`)];

    candidates.forEach((element) => {
      if (
        !(element instanceof Element) ||
        !element.classList.contains(animateClass) ||
        elementsSeen.has(element)
      ) {
        return;
      }

      elementsSeen.add(element);

      if (observer) {
        observer.observe(element);
      } else {
        reveal(element);
      }
    });
  };

  const startMutationObserver = () => {
    if (!supportsMutationObserver || mutationObserver) {
      return;
    }

    const target = document.body;

    if (!target) {
      return;
    }

    const fragmentCtor =
      typeof DocumentFragment !== 'undefined' ? DocumentFragment : null;

    const hasAnimateDescendant = (node) => {
      return (
        typeof node.querySelector === 'function' &&
        node.querySelector(`.${animateClass}`)
      );
    };

    mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            if (
              node.classList.contains(animateClass) ||
              hasAnimateDescendant(node)
            ) {
              observeElements(node);
            }
            return;
          }

          if (!fragmentCtor || !(node instanceof fragmentCtor)) {
            return;
          }

          if (hasAnimateDescendant(node)) {
            observeElements(node);
          }
        });
      });
    });

    mutationObserver.observe(target, {
      childList: true,
      subtree: true,
    });
  };

  const init = () => {
    observeElements();
    startMutationObserver();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) =>
    observeElements(event.target)
  );
  document.addEventListener('shopify:section:reorder', (event) =>
    observeElements(event.target)
  );
  document.addEventListener('shopify:section:select', (event) =>
    observeElements(event.target)
  );
  document.addEventListener('shopify:block:select', (event) =>
    observeElements(event.target)
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      observeElements();
    }
  });
})();
